import { c, t } from "ttag";

import { ActionIcon, Group, Icon, Text } from "metabase/ui";

const isLastPage = (pageIndex: number, pageSize: number, total: number) =>
  pageIndex === Math.ceil(total / pageSize) - 1;

export type PaginationControlsProps = {
  page: number;
  pageSize: number;
  itemsLength: number;
  total?: number;
  showTotal?: boolean;
  onNextPage?: (() => void) | null;
  onPreviousPage?: (() => void) | null;
  /** Override when a page can render more than one PaginationControls, so
   * each gets a distinct accessible name (WCAG 1.3.1, aria_landmark_name_unique). */
  "aria-label"?: string;
};

export const PaginationControls = ({
  page,
  pageSize,
  itemsLength,
  total,
  showTotal = false,
  onNextPage,
  onPreviousPage,
  "aria-label": ariaLabel = t`Pagination`,
  ...props
}: PaginationControlsProps) => {
  const isSinglePage = total !== undefined && total <= pageSize;

  if (isSinglePage) {
    return null;
  }

  const isPreviousDisabled = page === 0;
  const isNextDisabled =
    total != null ? isLastPage(page, pageSize, total) : !onNextPage;

  return (
    <Group
      align="center"
      fw="bold"
      aria-label={ariaLabel}
      role="navigation"
      {...props}
    >
      <Text component="span" mr="sm">
        {page * pageSize + 1} - {page * pageSize + itemsLength}
        {showTotal && (
          <>
            <Text component="span" c="text-secondary">
              &nbsp;
              {c(
                "Appears in phrases like '1-10 of 100', referring to a page of results",
              ).t`of`}
              &nbsp;
            </Text>
            <Text component="span" data-testid="pagination-total">
              {total}
            </Text>
          </>
        )}
      </Text>
      <ActionIcon
        variant="viewHeader"
        onClick={onPreviousPage ?? undefined}
        disabled={isPreviousDisabled}
        data-testid="previous-page-btn"
        aria-label={t`Previous page`}
      >
        <Icon name="chevronleft" />
      </ActionIcon>

      <ActionIcon
        variant="viewHeader"
        onClick={onNextPage ?? undefined}
        disabled={isNextDisabled}
        data-testid="next-page-btn"
        aria-label={t`Next page`}
      >
        <Icon name="chevronright" />
      </ActionIcon>
    </Group>
  );
};
